import { Component, Input, ViewChildren, QueryList, AfterViewInit, OnInit, input, effect, signal, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';

import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroChevronLeftMini } from '@ng-icons/heroicons/mini';
import { heroBuildingOffice2Solid, heroCalculatorSolid, heroClockSolid, heroUserGroupSolid } from '@ng-icons/heroicons/solid';

import { TASKS_STATUS_OPTIONS } from 'src/app/pages/tarefas/constants/status.constant';
import { TASKS_PERIOD_OPTIONS } from 'src/app/pages/tarefas/constants/period.constant';

import { iUser } from '@shared/contracts/interfaces/user.interface';
import { eTaskStatus } from 'src/app/pages/tarefas/contracts/enums/status.enum';
import { eTaskPeriod } from 'src/app/pages/tarefas/contracts/enums/period.enum';

import { ModulosDeFormulario } from 'src/app/utils/formsModules';
import { TasksFiltersService } from 'src/app/pages/tarefas/services/filters.service';
import { ApiUsuarioService } from 'src/app/services/api-usuario.service';

import { UsuariosComponent } from '../../sub-filtros/usuarios/usuarios.component';
import { StatusTarefaComponent } from '../../sub-filtros/status-tarefa/status-tarefa.component';
import { PeriodoTarefaComponent } from '../../sub-filtros/periodo-tarefa/periodo-tarefa.component';
import { CorretoraComponent } from '../../sub-filtros/corretora/corretora.component';

import { registerCloseOtherMenus } from '../../filtro-utils';
import { FiltroComponent } from '../../filtro.component';
@Component({
  selector: 'tasks-filters',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  providers: [
    provideIcons({
      heroChevronLeftMini,
      heroCalculatorSolid,
      heroClockSolid,
      heroUserGroupSolid,
      heroBuildingOffice2Solid,
    }),
  ],
  imports: [
    CommonModule,
    ModulosDeFormulario,
    MatTooltipModule,
    MatMenuModule,
    MatRadioModule,
    NgIcon,
    UsuariosComponent,
    StatusTarefaComponent,
    PeriodoTarefaComponent,
    CorretoraComponent,
  ],
})
export class TasksFiltersComponent implements OnInit, AfterViewInit {

  @Input() listaUsuarios!: iUser[];
  isMatriz = input<boolean>(false);
  corretoras = input<{ id: string; nomeFan: string }[]>([]);
  @Input() corretoraSelecionadaId: string = '';
  @ViewChildren(MatMenuTrigger) allTriggers!: QueryList<MatMenuTrigger>;

  public filtersService = input.required<TasksFiltersService>();

  protected status!: number;
  protected period!: number;
  protected usuariosID!: string[];

  protected filtroCorretora = signal<string>('');

  protected corretoraNome = signal<string>('');

  private get _matrizCorretoraId(): string {
    return this._apiUsuario.getCredenciais()?.corretoraId;
  }

  private get _matrizCorretoraNome(): string {
    return this._apiUsuario.getCredenciais()?.corretora || '';
  }

  constructor(
    private _apiUsuario: ApiUsuarioService,
    @Optional() private _filtroComp?: FiltroComponent
  ) {
    effect(() => {
      this.markOptionsAccordingToAppliedFilters();
    })
  }

  ngOnInit(): void {
    this.filtroCorretora.set(this.corretoraSelecionadaId || this._matrizCorretoraId || '');
    const nomeAlvo = this.filtersService().filtersSignal().corretoraNomeAlvo;
    this.corretoraNome.set(nomeAlvo || this._matrizCorretoraNome);
  }

  listaStatusTarefa = [
    { value: 0, viewValue: 'Todas', ativo: true }, // Opção "Todas" como padrão
    ...TASKS_STATUS_OPTIONS.slice(1).map(status => ({
      ...status,
      value: status.id,
      viewValue: status.text,
      ativo: false
    }))
  ];

  listaPeriodoTarefa = [
    { id: 0, text: 'Todos', ativo: true }, // Opção "Todos" como padrão
    ...TASKS_PERIOD_OPTIONS.slice(1).map(periodo => ({
      ...periodo,
      ativo: false
    }))
  ];

  private markOptionsAccordingToAppliedFilters = (): void => {
    const {status, startDate, endDate, usersID} = this.filtersService().filtersSignal();

    this.status = status ?? eTaskStatus.ALL;
    this.period = this.filtersService().getPeriodIdFromDates(startDate, endDate);
    this.usuariosID = usersID ?? [];
  }

  ngAfterViewInit() {
    registerCloseOtherMenus(this.allTriggers);
  }

  protected onChangeStatus = (event: {tarefa: eTaskStatus}): void => {
    this.filtersService().update({status: event.tarefa});
  }

  protected onChangePeriod = (event: {periodo: eTaskPeriod}): void => {
    const {startDate, endDate} = this.filtersService().preparePeriodDates(event.periodo);
    this.filtersService().update({startDate, endDate});
  }

  protected onChangeUser = (usersID: string[]): void => {
    this.filtersService().update({usersID})
  }

  protected onCorretoraChange = (corretora: { id: string; nomeFan: string }): void => {
    const corretoraId = corretora?.id;
    if (!corretoraId || corretoraId === this.filtroCorretora()) return;
    this.filtroCorretora.set(corretoraId);
    this.corretoraNome.set(corretora?.nomeFan ?? '');

    const ehMatriz = corretoraId === this._matrizCorretoraId;

    this.filtersService().update({
      corretoraIdAlvo: ehMatriz ? undefined : corretoraId,
      corretoraNomeAlvo: ehMatriz ? undefined : corretora?.nomeFan,
      usersID: []
    });
  }

  public clearFilter = (): void => {
    this.filtersService().clear();

    if (this.isMatriz()) {
      this.filtroCorretora.set(this._matrizCorretoraId || '');
      this.corretoraNome.set(this._matrizCorretoraNome);
    }
  }

  getStatusCount() {
    return this.status === 0 ? 0 : 1; // 0 para "Todas", 1 para qualquer status específico
  }

  getPeriodoCount() {
    return this.period === 0 ? 0 : 1; // 0 para "Todos", 1 para qualquer período específico
  }
}
